import { useCanViewEmails } from '../../../../../helpers/useCanViewEmails';
import React, { useEffect } from 'react';
import Select, { SingleValue } from '../../../AccessSelect';
import { Tooltip } from '@mui/material';
import cs from 'classnames';

import { USER_SHARE_OPTIONS } from '../../../MyFolderPage/ShareFolderModal/UserList';
import BellIconFilled from '../../../../../assets/icons/bell-button-filled.svg';
import { DownloadType, NewAccessType, RenotifyIdType } from '../../../MyFolderPage/ShareFolderModal';
import BellIcon from '../../../../../assets/icons/share-notify-off.svg';
import { RENOTIFY_BODY } from '../../../../../enums/NOTIFICATION';
import { ACCESS_TYPE } from '../../../../../enums/ACCESS_ENUM';
import { FILE_ACCESS_TYPE, GROUP, USER_NEW } from '../../../../../enums';
import TrashButton from '../../../../modules/TrashButton';
import RolePlate from '../../../../modules/RolePlate';
import css from '../ShareComposerModal.module.scss';
import Avatar from '../../../../modules/Avatar';
import { getDefaultShareAllowDownloads } from '../../../../../helpers';

type TUsersList = {
  newUsers: USER_NEW[];
  userList: FILE_ACCESS_TYPE[];
  newExcludedUsers?: (USER_NEW | GROUP)[];
  excludedUsersAccessList?: FILE_ACCESS_TYPE[];
  downloadPermissions?: DownloadType;
  onNewUserDownloadPermissionChange?: (userId: string, allowDownload: boolean) => void;
  onDownloadPermissionChange?: (accessObject: any, accessId: string, allowDownload: boolean) => void;
  newUsersAccess?: NewAccessType | null;
  onNewUserAccessChange: (id: string, e: SingleValue<{ value: string; label: ACCESS_TYPE }>) => void;
  onUserAccessChange: (id: string, e: SingleValue<{ value: string; label: ACCESS_TYPE }>) => void;
  onAccessDelete: (accessId: string) => void;
  onNewUserDelete: (userId: string) => void;
  addEntityToRenotify: (id: string, type: RenotifyIdType) => void;
  idsToRenotify: RENOTIFY_BODY | null;
  owner?: any;
};
const ShareComposerModalUserList: React.FC<TUsersList> = ({
  newUsers,
  userList,
  newExcludedUsers,
  excludedUsersAccessList,
  onNewUserAccessChange,
  onUserAccessChange,
  onAccessDelete,
  onNewUserDelete,
  onDownloadPermissionChange,
  onNewUserDownloadPermissionChange,
  newUsersAccess,
  downloadPermissions,
  addEntityToRenotify,
  idsToRenotify,
  owner,
}) => {
  const canViewEmails = useCanViewEmails();
  return (
    <div className={css.list}>
      {owner && (
        <div className={css.listItem} key={owner.id}>
          <div className={css.user}>
            <Avatar className={css.userAvatar} avatar={owner.avatar_url} name={owner.email} />
            <div className={css.userInfo}>
              <div className={css.userName}>{owner?.username}</div>
              {canViewEmails && (<div className={css.userEmail}>{owner.email}</div>)}
            </div>
            <div className={css.owner}>Current Owner</div>
          </div>
        </div>
      )}

      {newUsers.length > 0 &&
        newUsers.map((obj) => (
          <div
            className={cs(
              css.listItem,
              newExcludedUsers
                ?.map((user) => ('user_name' in user ? user.user.id : user.members?.map((member) => member.id)))
                .flat()
                .includes(obj.user.id) || excludedUsersAccessList?.map((user) => user.user.id).includes(obj.user.id)
                ? css.toDeleteItem
                : '',
            )}
            key={obj.user.id}
          >
            <div className={css.user}>
              <Avatar className={css.userAvatar} avatar={obj.user.avatar_url} name={obj.user.email} />
              <div className={css.userInfo}>
                <div className={css.userName}>{obj.user?.username}</div>
                {canViewEmails && (<div className={css.userEmail}>{obj.user.email}</div>)}
              </div>

              <div className={css.userRole}>
                <RolePlate className={css.rolePlate} userRole={obj.role.title} />
              </div>

              <div className={css.selectorContainer}>
                <Select
                  isSearchable={false}
                  defaultValue={USER_SHARE_OPTIONS[2]}
                  styles={{
                    control: (baseStyles) => ({
                      ...baseStyles,
                      background: '#525252',
                      border: '1px solid #DBDBDB',
                      borderRadius: '4px',
                      color: '#DBDBDB',
                      height: '24px',
                      padding: '0 8px',
                    }),
                  }}
                  options={USER_SHARE_OPTIONS}
                  unstyled
                  className={css.customSelector}
                  classNamePrefix="select"
                  name="access"
                  onChange={(e) => onNewUserAccessChange(obj.user.id, e)}
                  menuPosition={'fixed'}
                />
              </div>

              {!!(onNewUserDownloadPermissionChange && newUsersAccess) && (
                <div className={css.allowDownloadCheckbox}>
                  <div className={css.checkboxWrapper30}>
                    <div className={css.allowDownloadCheckboxText}>
                      Allow
                      <br />
                      Downloads?
                    </div>
                    <span className={css.checkbox}>
                      <input
                        type="checkbox"
                        checked={
                          newUsersAccess?.[obj.user.id]?.allowDownloads ??
                          getDefaultShareAllowDownloads(obj.role?.title || obj.user.role?.title)
                        }
                        onChange={(e) => onNewUserDownloadPermissionChange(obj.user.id, e.target.checked)}
                      />
                      <svg>
                        <use xlinkHref="#checkbox-30" className={css.checkbox}></use>
                      </svg>
                    </span>
                    <svg xmlns="http://www.w3.org/2000/svg" style={{ display: 'none' }}>
                      <symbol id="checkbox-30" viewBox="0 0 22 22">
                        <path
                          fill="none"
                          stroke="currentColor"
                          d="M5.5,11.3L9,14.8L20.2,3.3l0,0c-0.5-1-1.5-1.8-2.7-1.8h-13c-1.7,0-3,1.3-3,3v13c0,1.7,1.3,3,3,3h13 c1.7,0,3-1.3,3-3v-13c0-0.4-0.1-0.8-0.3-1.2"
                        />
                      </symbol>
                    </svg>
                  </div>
                </div>
              )}

              <div className={css.actionButtons}>
                <div className={css.removeBtn}>
                  <Tooltip title={'Delete'}>
                    <TrashButton small onClick={() => onNewUserDelete(obj.user.id)} />
                  </Tooltip>
                </div>
              </div>
            </div>
          </div>
        ))}

      {userList.map(
        (accessObject) =>
          accessObject?.user && (
            <div
              className={cs(
                css.listItem,
                newExcludedUsers
                  ?.map((user) => ('user_name' in user ? user.user.id : user.members?.map((member) => member.id)))
                  .flat()
                  .includes(accessObject.user.id) || excludedUsersAccessList?.map((user) => user.user.id).includes(accessObject.user.id)
                  ? css.toDeleteItem
                  : '',
              )}
              key={accessObject.user.id}
            >
              {/* <div className={css.listItem} key={accessObject.id}> */}
              <div className={css.user}>
                <Avatar className={css.userAvatar} avatar={accessObject.user.avatar_url} name={accessObject.user.email} />
                <div className={css.userInfo}>
                  <div className={css.userName}>{accessObject.user?.username}</div>
                  {canViewEmails && (<div className={css.userEmail}>{accessObject.user.email}</div>)}
                </div>

                <div className={css.userRole}>
                  <RolePlate className={css.rolePlate} userRole={accessObject.user.role.title} />
                </div>

                <div className={css.selectorContainer}>
                  <Select
                    isSearchable={false}
                    defaultValue={
                      USER_SHARE_OPTIONS[USER_SHARE_OPTIONS.findIndex((option) => option.value === accessObject?.access?.title)]
                    }
                    styles={{
                      control: (baseStyles) => ({
                        ...baseStyles,
                        background: '#525252',
                        border: '1px solid #DBDBDB',
                        borderRadius: '4px',
                        color: '#DBDBDB',
                        height: '24px',
                        padding: '0 8px',
                      }),
                    }}
                    options={USER_SHARE_OPTIONS}
                    unstyled
                    className={css.customSelector}
                    classNamePrefix="select"
                    name="access"
                    onChange={(e) => onUserAccessChange(accessObject.id, e)}
                    menuPosition={'fixed'}
                  />
                </div>

                {!!(onDownloadPermissionChange && downloadPermissions) && (
                  <div className={css.allowDownloadCheckbox}>
                    <div className={css.checkboxWrapper30}>
                      <div className={css.allowDownloadCheckboxText}>
                        Allow
                        <br />
                        Downloads?
                      </div>
                      <span className={css.checkbox}>
                        <input
                          type="checkbox"
                          checked={downloadPermissions?.[accessObject.id]?.allowDownload ?? !!accessObject.allow_downloads}
                          onChange={(e) => onDownloadPermissionChange(accessObject, accessObject.id, e.target.checked)}
                        />
                        <svg>
                          <use xlinkHref="#checkbox-30" className={css.checkbox}></use>
                        </svg>
                      </span>
                      <svg xmlns="http://www.w3.org/2000/svg" style={{ display: 'none' }}>
                        <symbol id="checkbox-30" viewBox="0 0 22 22">
                          <path
                            fill="none"
                            stroke="currentColor"
                            d="M5.5,11.3L9,14.8L20.2,3.3l0,0c-0.5-1-1.5-1.8-2.7-1.8h-13c-1.7,0-3,1.3-3,3v13c0,1.7,1.3,3,3,3h13 c1.7,0,3-1.3,3-3v-13c0-0.4-0.1-0.8-0.3-1.2"
                          />
                        </symbol>
                      </svg>
                    </div>
                  </div>
                )}

                <div className={css.actionButtons}>
                  <div className={css.removeBtn}>
                    <Tooltip title={'Delete'}>
                      <TrashButton small onClick={() => onAccessDelete(accessObject.id)} />
                    </Tooltip>
                  </div>
                  <Tooltip title={idsToRenotify?.userIds?.includes(accessObject.user.id) ? 'Re-notify on submit' : 'No notifications will be sent'}>
                    <img
                      src={idsToRenotify?.userIds?.includes(accessObject.user.id) ? BellIconFilled : BellIcon}
                      alt="notifications"
                      onClick={() => addEntityToRenotify(accessObject.user.id, RenotifyIdType.USER)}
                      className={css.bellIcon}
                    />
                  </Tooltip>
                </div>
              </div>
            </div>
          ),
      )}
    </div>
  );
};

export default ShareComposerModalUserList;
